import React, { useState } from "react";
import { Pencil, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inputCls } from "./EaUi";
import { COLORS, fmtEur, fmtSignedPct, pointSub } from "./eaFormat";
import { errMsg } from "./eaFixUtil";

const LABEL = { POWER: "Power", GAS: "Gas", ALL: "Totale portfolio" };
const btn = "h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2 disabled:opacity-50";
// Importi in formato italiano: "5.000.000", "1.234,56", "5000000", "€ 2.500"
const num = (t) => {
  let s = String(t ?? "").replace(/[€\s]/g, "");
  if (!s) return null;
  s = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : /^\d{1,3}(\.\d{3})+$/.test(s) ? s.replace(/\./g, "") : s;
  const n = Number(s);
  return Number.isNaN(n) ? NaN : n;
};
const s2 = (v) => (v == null ? "" : String(Math.round(v * 100) / 100).replace(".", ","));

export function DeltaCell({ v, pct }) {
  if (v == null) return <span className="text-slate-400">—</span>;
  const cls = v > 0 ? "text-red-600" : v < 0 ? "text-green-600" : "text-slate-600";
  return <span className={cls}>{v > 0 ? "+" : v < 0 ? "−" : ""}{fmtEur(Math.abs(v))} <span className="text-xs">({fmtSignedPct(pct)})</span></span>;
}

function TotalsEditor({ totals, tot, setTot }) {
  return (
    <div className="flex flex-wrap gap-4 px-4 pb-4" data-testid="ea-budget-totals-edit">
      {["POWER", "GAS"].filter((k) => totals[k]).map((k) => (
        <label key={k} className="text-xs text-slate-600">Budget totale {LABEL[k]} (€)
          <input value={tot[k] ?? s2(totals[k].budget)} onChange={(e) => setTot({ ...tot, [k]: e.target.value })} className={`${inputCls} w-44 mt-1 block text-right`} data-testid={`ea-budget-total-input-${k}`} />
          <span className="text-[11px] text-slate-400">es. 5.000.000 · ripartito sui punti in proporzione al budget Excel</span>
        </label>
      ))}
    </div>
  );
}

export function EaBudgetTable({ d, params, codes, onSaved }) {
  const { rows, totals } = d;
  const t = totals?.ALL;
  const [edit, setEdit] = useState(false);
  const [ed, setEd] = useState({});
  const [tot, setTot] = useState({});
  const [busy, setBusy] = useState(false);
  const close = () => { setEdit(false); setEd({}); setTot({}); };
  const call = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); close(); onSaved(); } catch (e) { toast.error(errMsg(e, "Salvataggio non riuscito")); }
    setBusy(false);
  };
  const save = () => {
    const totItems = Object.entries(tot).map(([k, v]) => [k, num(v)]).filter(([k, v]) => v !== totals[k]?.budget);
    const items = Object.entries(ed).map(([code, v]) => ({ code, budget_eur: num(v) }));
    if ([...totItems.map(([, v]) => v), ...items.map((i) => i.budget_eur)].some((v) => Number.isNaN(v) || v < 0)) return toast.error("Valori non validi");
    if (totItems.some(([, v]) => v == null)) return toast.error("Indica il budget totale");
    if (items.some((i) => i.budget_eur == null)) return toast.error("Indica il budget di ogni punto (usa Ripristina per tornare all'Excel)");
    if (totItems.length && items.length) return toast.error("Modifica i totali oppure i singoli punti, non entrambi insieme");
    const p = { ...params, codes };
    return call(async () => {
      for (const [k, v] of totItems) await api.put("/ea/budget/overrides/total", { year: d.year, commodity: k, total_eur: v }, { params: p });
      if (items.length) await api.put("/ea/budget/overrides", { year: d.year, items }, { params: p });
    }, "Budget aggiornato");
  };
  const reset = () => call(() => api.delete("/ea/budget/overrides", { params: { ...params, codes, year: d.year } }), "Budget ripristinati dal file Excel");
  const anyEdited = rows.some((r) => r.budget_edited);
  return (
    <div className="card-elevated overflow-x-auto" data-testid="ea-budget-table">
      <div className="p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-500 max-w-2xl">I budget modificati (evidenziati) prevalgono sul file Excel di controllo fatture anche dopo nuovi caricamenti, finché non li ripristini. Totali Power, Gas e complessivo si aggiornano di conseguenza.</div>
        <div className="flex gap-2">
          {!edit && <button onClick={() => setEdit(true)} className={btn} data-testid="ea-budget-edit"><Pencil size={14} /> Modifica budget</button>}
          {edit && <button onClick={save} disabled={busy} className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="ea-budget-save"><Save size={15} /> Salva</button>}
          {edit && <button onClick={close} className={btn} data-testid="ea-budget-cancel"><X size={14} /> Annulla</button>}
          {anyEdited && <button onClick={reset} disabled={busy} className={btn} data-testid="ea-budget-reset"><RotateCcw size={14} /> Ripristina da Excel</button>}
        </div>
      </div>
      {edit && <TotalsEditor totals={totals} tot={tot} setTot={setTot} />}
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{["Punto", "Commodity", "Budget annuale", "Forecast annuale", "Δ Forecast vs Budget", "Spesa fatturata YTD", "Mesi fatturati"].map((h, i) => <th key={h} className={`px-4 py-3 font-semibold ${i > 1 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody className="font-mono-nums">
          {rows.map((r) => (
            <tr key={r.code} className={`border-t border-slate-100 ${r.budget_edited ? "bg-amber-50" : "hover:bg-slate-50/60"}`} data-testid={`ea-budget-row-${r.identifier}`}>
              <td className="px-4 py-3 font-sans"><div className="font-semibold text-slate-900">{r.point_type} {r.identifier}</div><div className="text-xs text-slate-500 truncate max-w-[280px]">{pointSub(r)}</div></td>
              <td className="px-4 py-3 font-sans"><span className="inline-flex items-center gap-1.5 text-xs font-semibold"><span className="w-2 h-2 rounded-full" style={{ background: r.commodity === "POWER" ? COLORS.power : COLORS.gas }} />{LABEL[r.commodity]}</span></td>
              <td className="px-4 py-3 text-right" data-testid={`ea-budget-value-${r.identifier}`}>
                {edit ? <input value={ed[r.code] ?? s2(r.budget)} onChange={(e) => setEd({ ...ed, [r.code]: e.target.value })} className={`${inputCls} w-36 text-right`} data-testid={`ea-budget-input-${r.identifier}`} /> : fmtEur(r.budget)}
                {r.budget_edited && <div className="text-[11px] text-amber-700 font-sans">modificato · Excel {fmtEur(r.budget_file)}</div>}
              </td>
              <td className="px-4 py-3 text-right">{fmtEur(r.forecast)}</td>
              <td className="px-4 py-3 text-right"><DeltaCell v={r.delta} pct={r.delta_pct} /></td>
              <td className="px-4 py-3 text-right">{fmtEur(r.spend_ytd)}</td>
              <td className="px-4 py-3 text-right">{r.months_invoiced}/12</td>
            </tr>
          ))}
          {t && rows.length > 1 && (
            <tr className="border-t-2 border-slate-400 bg-slate-50 font-semibold text-slate-900" data-pdf-total data-testid="ea-budget-table-total">
              <td className="px-4 py-3 font-sans">Totale portfolio</td><td className="px-4 py-3 font-sans">{t.points} punti</td>
              <td className="px-4 py-3 text-right">{fmtEur(t.budget)}</td><td className="px-4 py-3 text-right">{fmtEur(t.forecast)}</td>
              <td className="px-4 py-3 text-right"><DeltaCell v={t.delta} pct={t.delta_pct} /></td><td className="px-4 py-3 text-right">{fmtEur(t.spend_ytd)}</td><td />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
